import { useCallback, useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import dayjs from 'shared/helpers/dayjs';
import {
  FilterValues,
  GroupingKey,
  Source,
  Urgency,
  Visibility,
} from 'events/components/FilterGroupingControl/_types';
import formatDateParam from 'events/helpers/dateFormat';

const PREFERENCES_STORAGE_KEY = 'nurau_events_home_preferences';

interface StoredPreferences {
  filters: FilterValues;
  grouping: GroupingKey;
}

interface UseEventsHomeUrlStateResult {
  selectedDate: dayjs.Dayjs;
  filters: FilterValues;
  grouping: GroupingKey;
  setSelectedDate: (date: dayjs.Dayjs) => void;
  setFilters: (filters: FilterValues) => void;
  setGrouping: (grouping: GroupingKey) => void;
}

const VALID_URGENCIES: Urgency[] = [
  'urgent',
  'needs_attention',
  'routine',
  'informational',
];
const VALID_VISIBILITIES: Visibility[] = ['private', 'shared'];
const VALID_SOURCES: Source[] = ['user', 'camera'];
const VALID_GROUPINGS: GroupingKey[] = [
  'default',
  'urgency',
  'status',
  'visibility',
];

function readEnum<T extends string>(
  raw: string | null,
  allowed: T[]
): T | null {
  if (!raw) return null;
  return (allowed as string[]).includes(raw) ? (raw as T) : null;
}

function readMaybeString(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function isDefaultPreferences({
  filters,
  grouping,
}: StoredPreferences): boolean {
  return (
    grouping === 'default' &&
    !filters.urgency &&
    !filters.department &&
    !filters.person &&
    !filters.tag &&
    !filters.visibility &&
    !filters.source &&
    !filters.signal
  );
}

function readStoredPreferences(): StoredPreferences | null {
  try {
    const raw = window.localStorage.getItem(PREFERENCES_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as {
      filters?: Partial<Record<keyof FilterValues, unknown>>;
      grouping?: unknown;
    };
    return {
      filters: {
        urgency: readEnum(
          readMaybeString(parsed.filters?.urgency),
          VALID_URGENCIES
        ),
        department: readMaybeString(parsed.filters?.department),
        person: readMaybeString(parsed.filters?.person),
        tag: readMaybeString(parsed.filters?.tag),
        visibility: readEnum(
          readMaybeString(parsed.filters?.visibility),
          VALID_VISIBILITIES
        ),
        source: readEnum(
          readMaybeString(parsed.filters?.source),
          VALID_SOURCES
        ),
        signal: readMaybeString(parsed.filters?.signal),
      },
      grouping:
        readEnum(readMaybeString(parsed.grouping), VALID_GROUPINGS) ??
        'default',
    };
  } catch {
    return null;
  }
}

function writeStoredPreferences(prefs: StoredPreferences): void {
  try {
    if (isDefaultPreferences(prefs)) {
      window.localStorage.removeItem(PREFERENCES_STORAGE_KEY);
    } else {
      window.localStorage.setItem(
        PREFERENCES_STORAGE_KEY,
        JSON.stringify(prefs)
      );
    }
  } catch {
    // localStorage may be unavailable (private mode, quota); persistence is best-effort.
  }
}

export default function useEventsHomeUrlState(): UseEventsHomeUrlStateResult {
  const history = useHistory();
  const location = useLocation();

  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const selectedDate = useMemo(() => {
    const dateFromUrl = params.get('date');
    if (dateFromUrl) {
      const parsed = dayjs(dateFromUrl);
      if (parsed.isValid()) return parsed.startOf('day');
    }
    return dayjs().startOf('day');
  }, [params]);

  const filters = useMemo<FilterValues>(
    () => ({
      urgency: readEnum(params.get('urgency'), VALID_URGENCIES),
      department: params.get('department'),
      person: params.get('person'),
      tag: params.get('tag'),
      visibility: readEnum(params.get('visibility'), VALID_VISIBILITIES),
      source: readEnum(params.get('source'), VALID_SOURCES),
      signal: params.get('signal'),
    }),
    [params]
  );

  const grouping = useMemo<GroupingKey>(
    () => readEnum(params.get('grouping'), VALID_GROUPINGS) ?? 'default',
    [params]
  );

  const hasFilterOrGroupingParams = useMemo(
    () =>
      params.has('urgency') ||
      params.has('department') ||
      params.has('person') ||
      params.has('tag') ||
      params.has('visibility') ||
      params.has('source') ||
      params.has('signal') ||
      params.has('grouping'),
    [params]
  );

  const buildSearch = useCallback(
    (next: {
      date?: dayjs.Dayjs;
      filters?: FilterValues;
      grouping?: GroupingKey;
    }) => {
      const out = new URLSearchParams();
      const date = next.date ?? selectedDate;
      if (!date.isSame(dayjs().startOf('day'), 'day')) {
        out.set('date', formatDateParam(date));
      }
      const f = next.filters ?? filters;
      if (f.urgency) out.set('urgency', f.urgency);
      if (f.department) out.set('department', f.department);
      if (f.person) out.set('person', f.person);
      if (f.tag) out.set('tag', f.tag);
      if (f.visibility) out.set('visibility', f.visibility);
      if (f.source) out.set('source', f.source);
      if (f.signal) out.set('signal', f.signal);
      const g = next.grouping ?? grouping;
      if (g !== 'default') out.set('grouping', g);
      const s = out.toString();
      return s ? `?${s}` : '';
    },
    [selectedDate, filters, grouping]
  );

  useEffect(() => {
    if (hasFilterOrGroupingParams) return;
    const stored = readStoredPreferences();
    if (!stored || isDefaultPreferences(stored)) return;
    history.replace({
      pathname: location.pathname,
      search: buildSearch({
        filters: stored.filters,
        grouping: stored.grouping,
      }),
    });
  }, [hasFilterOrGroupingParams, history, location.pathname, buildSearch]);

  const setSelectedDate = useCallback(
    (date: dayjs.Dayjs) => {
      history.replace({
        pathname: location.pathname,
        search: buildSearch({ date }),
      });
    },
    [history, location.pathname, buildSearch]
  );

  const setFilters = useCallback(
    (nextFilters: FilterValues) => {
      writeStoredPreferences({ filters: nextFilters, grouping });
      history.replace({
        pathname: location.pathname,
        search: buildSearch({ filters: nextFilters }),
      });
    },
    [history, location.pathname, buildSearch, grouping]
  );

  const setGrouping = useCallback(
    (nextGrouping: GroupingKey) => {
      writeStoredPreferences({ filters, grouping: nextGrouping });
      history.replace({
        pathname: location.pathname,
        search: buildSearch({ grouping: nextGrouping }),
      });
    },
    [history, location.pathname, buildSearch, filters]
  );

  return {
    selectedDate,
    filters,
    grouping,
    setSelectedDate,
    setFilters,
    setGrouping,
  };
}
